import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { Navigate, useSearchParams, useNavigate } from 'react-router-dom';
import { Users, Shield, CheckCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

const Onboarding = () => {
  const { user, loading } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const invitationToken = searchParams.get('invitation');
  const familyName = searchParams.get('family');
  const email = searchParams.get('email');

  useEffect(() => {
    // Process invitation when user is authenticated and has invitation token
    if (user && invitationToken) {
      processInvitation();
    }
  }, [user, invitationToken]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Redirect if no user or no invitation token
  if (!user || !invitationToken) {
    return <Navigate to="/auth" replace />;
  }

  const processInvitation = async () => {
    if (!invitationToken) return;

    setIsLoading(true);
    try {
      // Verify the user's email matches the invitation
      if (user?.email !== email) {
        toast({
          title: 'Erreur d\'invitation',
          description: `Cette invitation est pour ${email}, mais vous êtes connecté avec ${user?.email}. Veuillez vous connecter avec le bon compte.`,
          variant: 'destructive',
        });
        setIsLoading(false);
        return;
      }

      const { data, error } = await supabase.functions.invoke('confirm-invitation', {
        body: { token: invitationToken }
      });

      if (error) {
        console.error('Error confirming invitation:', error);
        let errorMessage = "Impossible d'accepter l'invitation.";
        
        if (error.message?.includes('expired')) {
          errorMessage = "Cette invitation a expiré. Demandez une nouvelle invitation.";
        } else if (error.message?.includes('not found')) {
          errorMessage = "Cette invitation n'existe pas ou a déjà été utilisée.";
        } else if (error.message?.includes('email')) {
          errorMessage = "L'email ne correspond pas à l'invitation.";
        }
        
        toast({
          title: 'Erreur d\'invitation',
          description: errorMessage,
          variant: 'destructive',
        });
        return;
      }

      // Silent invitation confirmation - no toast

      navigate('/dashboard');
    } catch (err: any) {
      console.error('Error invoking confirm-invitation:', err);
      toast({
        title: 'Erreur',
        description: 'Une erreur inattendue s\'est produite lors de l\'acceptation de l\'invitation.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompleteProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast({
        title: "Erreur",
        description: "Les mots de passe ne correspondent pas.",
        variant: "destructive",
      });
      return;
    }

    if (password.length < 6) {
      toast({
        title: "Erreur",
        description: "Le mot de passe doit contenir au moins 6 caractères.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    
    try {
      // Update user profile with names
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          first_name: firstName.trim(),
          last_name: lastName.trim()
        })
        .eq('user_id', user.id);

      if (profileError) throw profileError;

      // Update user password
      const { error: passwordError } = await supabase.auth.updateUser({
        password: password
      });

      if (passwordError) throw passwordError;

      toast({
        title: "Profil complété",
        description: "Votre profil a été mis à jour avec succès.",
      });

      // Process invitation after profile completion
      await processInvitation();
      
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="bg-card rounded-full p-3 shadow-medium">
              <Shield className="h-8 w-8 text-primary" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-card mb-2">Clan</h1>
          <p className="text-card/80">Complétez votre profil pour continuer</p>
          {familyName && (
            <div className="mt-4">
              <Badge variant="secondary" className="bg-card/10 text-card border-card/20">
                <CheckCircle className="h-4 w-4 mr-2" />
                Invitation pour la famille "{familyName}"
              </Badge>
            </div>
          )}
        </div>

        <Card className="shadow-large">
          <CardHeader className="text-center">
            <CardTitle className="flex items-center justify-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              Complétez votre profil
            </CardTitle>
            <CardDescription>
              Ajoutez vos informations pour rejoindre {familyName ? `la famille "${familyName}"` : 'votre famille'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCompleteProfile} className="space-y-4">
              {email && (
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input value={email} disabled className="bg-muted" />
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="firstname">Prénom *</Label>
                  <Input
                    id="firstname"
                    type="text"
                    placeholder="Prénom"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastname">Nom *</Label>
                  <Input
                    id="lastname"
                    type="text"
                    placeholder="Nom"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Créer un mot de passe *</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Minimum 6 caractères"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirmer le mot de passe *</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  placeholder="Retapez le mot de passe"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full" 
                disabled={isLoading}
              >
                {isLoading ? "Finalisation..." : "Rejoindre la famille"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Onboarding;